import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from "react";
import SubmitNewSpace from '../../graphql/SubmitNewSpace.graphql';
import { Space } from "../../types/Space";
import { SpaceInput } from "../../types/SpaceInput";
import { gql, useMutation } from "@apollo/client";
import { useRouter } from 'next/router';
import { ProfilePrivacy } from "../../types/ProfilePrivacy";
import { constants } from "../../constants";
import { MediaType } from "../../types/MediaReference";
import { faExclamationCircle, faSignIn } from "@fortawesome/pro-light-svg-icons";
import Head from 'next/head';
import SpaceEditor, { SpaceEditorValues } from '../../components/space/SpaceEditor';
import ErrorDisplay from '../../components/ErrorDisplay';
import { useSession, signIn } from 'next-auth/react';

export default function CreateSpace() {
    const router = useRouter();
    const { data: session, status } = useSession();

    const [createSpace, { error, loading }] = useMutation<{ addSpace: Space }, { space: SpaceInput }>(SubmitNewSpace, { onError: () => { } });
    const saveCreateSpace = (values: SpaceEditorValues) => {
        createSpace({
            variables: {
                space: {
                    profileType: values.profileType,
                    name: values.name,
                    location: values.location,
                    locationVisible: values.locationVisible,
                    yearOfBirth:
                        typeof values.yearOfBirth === 'number'
                            ? values.yearOfBirth
                            : values.yearOfBirth ? parseInt(values.yearOfBirth, 10) : undefined,
                    yearOfDeath:
                        typeof values.yearOfDeath === 'number'
                            ? values.yearOfDeath
                            : values.yearOfDeath ? parseInt(values.yearOfDeath, 10) : undefined,
                    profilePicture: values.profilePicture
                        ? {
                            url: values.profilePicture?.hostFilename!,
                            mediaType: MediaType.Image
                        }
                        : undefined,
                    background: values.background
                        ? {
                            url: values.background.hostFilename!,
                            mediaType: MediaType.Image
                        }
                        : undefined,
                    obituary: values.obituary,
                    privacy: values.privacy,
                    moderators: values.moderators || [],
                    administrators: values.administrators || []
                }
            },
            
            update(cache, { data: data }) {
                cache.writeFragment({
                    data: data?.addSpace,
                    fragment: gql`fragment NewSpace on Space { id }`
                });
                router.push('/spaces/' + data?.addSpace.id);
            }
        });
    }

    var defaultProfile: Space = {
        locationVisible: true,
        profilePrivacy: ProfilePrivacy.Public
    };

    if (!router.isReady)
        return null;

    if (status === 'loading') return null;

    if (status === 'unauthenticated' || !session) return (
        <>
            <Head>
                <title>Access Denied - {constants.siteTitle}</title>
            </Head>

            <div className='mx-auto container max-w-screen-lg py-12 px-4 text-center'>
                <div className='flex items-center justify-center'>
                    <FontAwesomeIcon icon={faExclamationCircle} size='2x' className='text-loving-400 mr-3' />
                    <div className='text-medium text-gray-700'>You will need to sign in or register for an account first.</div>
                </div>
                <button className='btn-outline inline-flex items-center mt-8' onClick={() => signIn('azure-ad-b2c')}>
                    <FontAwesomeIcon icon={faSignIn} className='text-loving-500 mr-2' />
                    <span className='text-sm font-medium'>Sign in</span>
                </button>
            </div>
        </>
    )

    return (
        <>
            <Head>
                <title>Create a place - {constants.siteTitle}</title>
            </Head>
            <div className='mx-auto container max-w-screen-lg pb-10 px-4 md:px-8'>
                <div className='my-8 text-center lg:mt-16 lg:text-left'>
                    <h1 className='text-5xl font-semilight text-oswalds-800 mb-8'>Create a place</h1>
                </div>
                <SpaceEditor space={defaultProfile} isNew={true} isSaving={loading} saveSpace={saveCreateSpace} />
                    {/* @ts-ignore - error message is burried in networkError.result, but the TS declaration doesn't have that property */}
                    {error && error.networkError?.result != undefined && (
                        <div className='max-w-lg mt-6'>
                            {/* @ts-ignore */}
                            <ErrorDisplay errors={error.networkError.result.errors.map(error => error.message)} />
                        </div>
                    )}
            </div>
        </>
    );
}
